import React, { useEffect, useState, useCallback } from "react";
import { TL } from "../i18n";
import { useSearchParams } from "react-router-dom";
import client, { apiErr } from "../lib/api";
import { Button, Card, Badge } from "../components/ui";import { Receipt, RefreshCw, CheckCircle2, ExternalLink, ShieldAlert, Loader2 } from "lucide-react";
import ProfileForm from "../components/ProfileForm";

const fmt = (n) => (n == null ? "—" : `USD ${Number(n).toFixed(2)}`);
const day = (d) => (d ? String(d).slice(0, 10) : "—");

export default function MyPurchases() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => client.get("/account/purchases").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  // settle account-service checkout return
  useEffect(() => {
    const sid = params.get("service_session_id");
    if (!sid) return;
    client.get(`/payments/status/${sid}`).then(({ data: d }) => {
      if (d.payment_status === "paid") setMsg("Account service charge paid — thank you!");
      setParams({}, { replace: true }); load();
    }).catch(() => setParams({}, { replace: true }));
  }, [params, setParams, load]);

  const payService = async () => {
    setBusy(true);
    try {
      const { data: d } = await client.post("/payments/account-service-checkout", { origin_url: window.location.origin });
      if (d?.url) window.location.href = d.url; else setMsg("Could not start service checkout.");
    } catch (e) { setMsg(apiErr(e)); setBusy(false); }
  };

  if (!data) return <div className="grid place-items-center py-20"><Loader2 className="animate-spin opacity-50" /></div>;
  const svc = data.service || {};

  return (
    <div className="space-y-6" data-testid="my-purchases">
      <div>
        <h1 className="font-head text-3xl">{TL("My Purchases & Receipts")}</h1>
        <p className="text-sm opacity-70">{TL("Your applications, payments, receipts and account service status.")}</p>
      </div>
      {msg && <Card className="border-[var(--primary)]/40 text-sm" data-testid="mp-msg">{msg}</Card>}

      {svc.active && (
        <Card data-testid="mp-service">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-head text-lg">{TL("Annual account service")}</div>
              <div className="text-sm opacity-70">{TL("USD 90.00/yr — covers all your apps (updates, newsletter & customer service).")}</div>
              <div className="mt-1 text-sm">
                {TL("Status:")} {svc.expired
                  ? <Badge color="danger">{TL("OVERDUE")}</Badge>
                  : svc.due_soon ? <Badge color="warning">{TL("DUE SOON")}</Badge> : <Badge color="success">{TL("ACTIVE")}</Badge>}
                <span className="ms-2 opacity-70">{svc.expires_at ? `${TL("through")} ${day(svc.expires_at)}` : ""}</span>
              </div>
            </div>
            <Button onClick={payService} disabled={busy} data-testid="mp-pay-service">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} {TL("Pay / renew service")}
            </Button>
          </div>
        </Card>
      )}

      <Card>
        <div className="mb-3 flex items-center gap-2 font-head text-lg"><Receipt size={18} /> {TL("Payment history")}</div>
        {data.purchases.length === 0 && <div className="text-sm opacity-60" data-testid="mp-empty">{TL("No purchases yet.")}</div>}
        <div className="space-y-2">
          {data.purchases.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-black/10 dark:border-white/10 p-3 text-sm" data-testid={`mp-row-${p.id}`}>
              <div className="min-w-0">
                <div className="font-medium">{p.label}{p.test && <Badge color="warning" className="ms-2">{TL("TEST")}</Badge>}</div>
                <div className="text-xs opacity-60">{day(p.paid_at || p.created_at)} · {p.kind}{p.units > 1 ? ` · ${p.units} apps` : ""}</div>
              </div>
              <div className="text-end">
                <div className="font-semibold">{fmt(p.amount)}</div>
                {p.discount_amount > 0 && <div className="text-xs opacity-60">saved {fmt(p.discount_amount)} ({p.discount_pct}% off)</div>}
              </div>
              <div className="flex items-center gap-2">
                {p.status === "paid" ? <Badge color="success">{TL("PAID")}</Badge> : <Badge color="warning">{(p.status || "pending").toUpperCase()}</Badge>}
                {p.receipt_url && <a href={p.receipt_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[var(--primary)] hover:underline" data-testid={`mp-receipt-${p.id}`}>{TL("Receipt")} <ExternalLink size={12} /></a>}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <div className="mb-1 flex items-center gap-2 font-head text-lg"><CheckCircle2 size={18} /> {TL("Customer-service profile")}</div>
        <p className="mb-3 text-sm opacity-70">{TL("Keep this current so our team can support you accurately.")}</p>
        <ProfileForm initial={data.profile} initialConsents={data.consents} onSaved={load} />
      </Card>
    </div>
  );
}
